import React, { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { useWindowSize } from 'usehooks-ts';
import Button from '~/components/form-elements/button';
import { defaultLocale } from '~/locale/defaults';
import { color, Heading1, Heading2, Heading5, media, spacing, typography } from '~/theme';
import { cxpSignIn } from '~/utils/auth';
import { urlWithBasePath } from '~/utils/url-helpers';
import { LeadParagraph, TextParagraph } from '~/theme/elements';

export const Banner = styled.div`
    width: 100%;
    height: 523px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    background-size: cover;
    background-repeat: no-repeat;
    background-image: url('${urlWithBasePath('/images/splash-banner-mobile.png')}');

    @media ${media.small} {
        background-image: url('${urlWithBasePath('/images/splash-banner.png')}');
    }
`;

const SignInSection = styled.div`
    padding: ${spacing.spacing7} calc(${spacing.spacing6} - 1px);
    opacity: 80%;
    background-color: ${color.palette.black};
`;

const Content = styled.div`
    button {
        margin: ${spacing.spacing0};
    }

    @media ${media.small} {
        max-width: 358px;
    }

    @media ${media.medium} {
        max-width: 446px;
    }
`;

const StyledHeading = styled(Heading1)`
    color: ${color.background};
    margin: ${spacing.spacing0};
    margin-bottom: ${spacing.spacing5};
`;

const StyledText = styled(TextParagraph)`
    margin-top: ${spacing.spacing5};
    margin-bottom: ${spacing.spacing0};
    color: ${color.background};
`;

const WhyCreateAccountSection = styled.div``;

const WhyCreateAccountHeader = styled.div`
    background-color: ${color.palette.serviceOntarioGreen};
    padding: ${spacing.spacing5} calc(${spacing.spacing6} - 1px);
    display: flex;
    flex-direction: column;
    text-align: center;
    color: ${color.palette.white};

    @media ${media.small} {
        align-items: center;
    }
`;

const WhyCreateAccountContent = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    padding: ${spacing.spacing5} ${spacing.spacing7} ${spacing.spacing4} ${spacing.spacing7};
    background-color: ${color.palette.greyscale5};

    @media ${media.small} {
        flex-direction: row;
        padding: ${spacing.spacing7} ${spacing.spacing12};
    }
`;

const WhyCreateAccountLeadText = styled(TextParagraph)`
    font-size: ${typography.defaultFontSize};
    margin-bottom: ${spacing.spacing2};
`;
const WhyCreateAccountTile = styled.div`
    display: flex;
    justify-content: center;
    margin: ${spacing.spacing4} ${spacing.spacing0} ${spacing.spacing0} ${spacing.spacing0};
    flex-direction: column;
    align-items: center;
    text-align: center;

    @media ${media.small} {
        margin: ${spacing.spacing0} ${spacing.spacing4};
        width: 100%;
    }
`;
const WhyCreateAccountImage = styled.img`
    width: 48px;
    margin-bottom: ${spacing.spacing2};
    @media ${media.small} {
        width: unset;
    }
`;

const StyledItemText = styled(LeadParagraph)`
    font-size: ${typography.defaultFontSize}px;
`;

type itemType = { image: string; heading: string; text: string };

function SplashBanner() {
    const { status } = useSession();
    const { locale } = useRouter();
    const windowSize = useWindowSize();
    const { t } = useTranslation();

    const signIn = () => {
        cxpSignIn('/verify-user', locale ?? defaultLocale);
    };

    useEffect(() => {
        const mainElement = document.getElementById('content');
        if (mainElement) {
            const style =
                (mainElement as any)?.currentStyle ?? window.getComputedStyle(mainElement);
            const marginLeft = style?.marginLeft;
            const signInSection = document.getElementById('signInSection');
            if (signInSection && marginLeft) {
                signInSection.style.paddingLeft = `${
                    parseInt(marginLeft.split('px')[0], 10) + 16
                }px`;
            }
        }
    }, [windowSize]);

    const scrollToRegistrationSection = () => {
        document.getElementById('before-you-register')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <>
            <Banner>
                {status !== 'authenticated' && (
                    <SignInSection id="signInSection">
                        <Content>
                            <StyledHeading>{t('splash:banner.heading')}</StyledHeading>
                            <Button onClick={signIn}>{t('splash:banner.signInButtonText')}</Button>
                            <StyledText onClick={scrollToRegistrationSection}>
                                {t('splash:banner.alreadyRegisteredText')}
                            </StyledText>
                        </Content>
                    </SignInSection>
                )}
            </Banner>
            <WhyCreateAccountSection>
                <WhyCreateAccountHeader>
                    <Heading2>{t('splash:banner.whyCreateAccount.heading')}</Heading2>
                    <WhyCreateAccountLeadText>
                        {t('splash:banner.whyCreateAccount.whatIsAcccount')}
                    </WhyCreateAccountLeadText>
                </WhyCreateAccountHeader>
                <WhyCreateAccountContent>
                    {(
                        t('splash:banner.whyCreateAccount.items', {
                            returnObjects: true,
                        }) as itemType[]
                    ).map((item, index) => (
                        <WhyCreateAccountTile key={`whyCreateAccountTile-${index}`}>
                            <WhyCreateAccountImage
                                src={urlWithBasePath(item.image)}
                                alt={item.heading}
                                key={`whyCreateAccount-${index}`}
                            />
                            <Heading5 key={`whyCreateAccountHeading-${index}`}>
                                {item.heading}
                            </Heading5>
                            <StyledItemText key={`whyCreateAccountText-${index}`}>
                                {item.text}
                            </StyledItemText>
                        </WhyCreateAccountTile>
                    ))}
                </WhyCreateAccountContent>
            </WhyCreateAccountSection>
        </>
    );
}

export default SplashBanner;
